// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DealerData, DealerStatus } from '@interfaces/dealer.interfaces';
import {
    UserPermissionIds,
    User,
    UserTypes,
    UserAuthState,
    UserSignatureCall,
    DealerAppPermissions,
    UserNotificationType,
    UserLogonState,
    JobTitle,
    UserDealershipTerminationDetails,
    GetUserByEmailResponse,
    AppPermission,
} from '@interfaces/user.interfaces';
import { BehaviorSubject, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../login/services/auth.service';
import { ApiService } from './api.service';
import { ErrorService } from './error.service';
import { RpcApiError, RpcApiResponse } from '@interfaces/api.interfaces';
import { ChangeSignatureModalComponent } from '../modals/change-signature-modal/change-signature-modal.component';
import { formatSignatureRequest } from '../utils/format-util';
import { MatDialog } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SystemDocument } from '@interfaces/document.interfaces';
import { DealershipService } from './dealership.service';
import { PaginatedData } from '@interfaces/interfaces';
import { LeadStatus } from '@interfaces/lead.interfaces';
import { DealStatuses, JacketType } from '@interfaces/deal.interfaces';
import { LoginService } from '../login/services/login.service';
import { SnackbarService } from './snackbar.service';

@Injectable({ providedIn: 'root' })
export class UserService {
    private _user: User = <User>{};
    private _user$: BehaviorSubject<User> = new BehaviorSubject(<User>{});
    private _userState$ = new BehaviorSubject<UserAuthState>(UserAuthState.init);
    private _versionNumber$ = new BehaviorSubject<string>(null);
    private filterState = new BehaviorSubject<{ leads?: LeadStatus[]; deals?: DealStatuses[]; consumerDocs?: DealStatuses[]; storageJackets?: DealStatuses[] }>(
        sessionStorage.getItem('filter-state') ? JSON.parse(sessionStorage.getItem('filter-state')) : {}
    ); // This has to live here to accommodate a clear on logout. Cannot import navService into this file.

    get isInternal() {
        return this._user?.userType === UserTypes.Internal;
    }

    get isCustomer() {
        return this._user?.userType === UserTypes.Customer;
    }

    get isDealer() {
        return this._user.userType === UserTypes.Dealer;
    }

    get user() {
        return { ...this._user };
    }

    get userNotificationType() {
        let notification = UserNotificationType.Both;

        if (this.user.emailAlerts && !this.user.textAlerts) {
            notification = UserNotificationType.Email;
        } else if (!this.user.emailAlerts && this.user.textAlerts) {
            notification = UserNotificationType.Text;
        } else if (this.user.emailAlerts && this.user.textAlerts) {
            notification = UserNotificationType.Both;
        }

        return notification;
    }

    user$() {
        return this._user$.asObservable();
    }

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly apiService: ApiService,
        private readonly authService: AuthService,
        private readonly loginService: LoginService,
        private readonly errorService: ErrorService,
        private readonly deviceService: DeviceDetectorService,
        private readonly dealershipService: DealershipService,
        private readonly snackbarService: SnackbarService,
        private readonly router: Router,
        private readonly dialog: MatDialog
    ) {
        this.resetUser();
        this._user$.subscribe((user) => (this._user = user));

        this.authService
            .isAuthenticated$()
            .pipe(
                tap((isAuth) => this._userState$.next(isAuth ? UserAuthState.authenticated : UserAuthState.loggedOut)),
                filter((isAuth) => isAuth),
                switchMap(() => forkJoin([this.getUser$base(), this.getVersionNumber()])),
                catchError((error) => {
                    this._userState$.next(UserAuthState.loggedOut);
                    this.authService.goToLogin();
                    return of(error);
                })
            )
            .subscribe();
    }

    getDealerId() {
        return this._user$.getValue().dealerId;
    }

    checkPermission$(userRole: UserTypes, appPermission: UserPermissionIds): Observable<boolean> {
        return this.isUserRole$(userRole).pipe(
            map((isRole) => {
                return isRole && this.isUserCurrentDealerPermission()?.userPermissionId.some((x) => x === appPermission);
            })
        );
    }

    hasPermission$(userRoles: UserTypes[], appPermissions: UserPermissionIds[]): boolean {
        const hasRoleAccess = userRoles.some((role) => this._user$.value?.userType === role);
        const hasPermissionAccess = appPermissions.some((permission: any) =>
            this.isUserCurrentDealerPermission()?.userPermissionId.some((x) => {
                return x === permission || (permission.length > 0 && permission.includes(x));
            })
        );

        return hasRoleAccess && hasPermissionAccess;
    }

    isInternal$() {
        return this.isUserRole$(UserTypes.Internal);
    }

    isCustomer$() {
        return this.isUserRole$(UserTypes.Customer);
    }

    isDealer$() {
        return this.isUserRole$(UserTypes.Dealer);
    }

    isUserCurrentDealerPermission(): DealerAppPermissions {
        return (this._user$.value.dealerAppPermissions || []).find((x) => x.dealerId === this._user$.value.dealerId);
    }

    isUserRole$(userRole: UserTypes) {
        return this._user$.asObservable().pipe(map((user) => user?.userType === userRole));
    }

    isUserProfileComplete() {
        return this._user$.value.needProfileUpdate ? false : true;
    }

    userNeedsToAcceptSystemDocuments() {
        return this._user$.value.needToAcceptSystemDocument ? false : true;
    }

    userHasMissingSignatures(dealId: number, docId: number): boolean {
        let missingSignatures = false;
        if (
            this._user$.value.signature === undefined ||
            this._user$.value.signature === null ||
            this._user$.value.initials === undefined ||
            this._user$.value.initials === null
        ) {
            missingSignatures = true;
            this.routeHasMissingSignaturesError(docId, dealId);
        }

        return missingSignatures;
    }

    routeHasMissingSignaturesError(docId?: number, dealId?: number, currentPage: string = null): void {
        let message = 'You have not yet set up your legal name,signature or initials, which are required to sign documents. Click Okay to do that now.';
        if (currentPage === 'interactionInt') {
            //From the pdf interaction service can load from signing or F and I forms.
            message = 'You have not yet set up your legal name,signature or initials, which are required to sign documents. Click Okay to do that now.';
            if (this.activatedRoute?.snapshot['_routerState']?.url.includes('/pddj') === true) {
                return;
            }
        } else {
            message =
                'It appears one or more of your signatures did not save properly, or did not meet our size requirements. Please click "Okay" to update your signatures.';
        }

        const error: RpcApiError = {
            button: 'Okay',
            statusCode: 550,
            message: message,
            title: 'Incomplete Set Up for Deal Documents',
        };
        this.errorService
            .showErrorAtService(error)
            .afterClosed()
            .subscribe((val) => {
                if (this._user$.value.userType === UserTypes.Customer) {
                    this.router.navigate(['/main', 'settings'], {
                        queryParams: {
                            docId: docId,
                            dealId: dealId,
                            previousPage: currentPage !== 'brokenSignature' ? currentPage : 'customerSigning',
                        },
                    });
                } else if (this._user$.value.userType === UserTypes.Dealer || this._user$.value.userType === UserTypes.Internal) {
                    this.router.navigate(['/dashboard', 'settings'], {
                        queryParams: {
                            docId: docId,
                            dealId: dealId,
                            previousPage: currentPage !== 'brokenSignature' ? currentPage : 'signing',
                        },
                    });
                }
            });
    }

    logOut() {
        this.authService.signOut();
        this.resetUser();
        this.setFilterState();
        this.dealershipService.clearDealers();
        this._userState$.next(UserAuthState.loggedOut);
    }

    versionNumber$(): Observable<string> {
        return this._versionNumber$.asObservable();
    }

    userState$(): Observable<UserAuthState> {
        return this._userState$.asObservable();
    }

    // system docs (terms of service/privacy policy) for display on help page
    getHelpPageSystemDocuments(): Observable<SystemDocument[]> {
        return this.apiService.getV2$<SystemDocument[]>('api/Messages/systemDocuments', null, false).pipe(map((response) => response?.data));
    }

    // for new user set up & when the documents that have already been agreed to have an updated version
    getReleaseNotes(systemDocumentId: number): Observable<any> {
        return this.apiService.getV2$(`api/Messages/allSystemDocuments?systemDocumentTypeId=${systemDocumentId}`).pipe(map((response) => response?.data));
    }

    // for footer
    getVersionNumber(): Observable<string> {
        return this.apiService.getV2$<string>('api/Messages/versionNumber', null, false).pipe(
            map((response) => {
                this._versionNumber$.next(response?.data);
                return response?.data;
            })
        );
    }

    // for new user set up & when the documents that have already been agreed to have an updated version
    getRequiredSystemDocuments(): Observable<SystemDocument[]> {
        return this.apiService.getV2$<SystemDocument[]>('api/user/systemDocuments').pipe(map((response) => response?.data));
    }

    // for new user set up & when the documents that have already been agreed to have an updated version
    getDPA(token: string): Observable<SystemDocument[]> {
        return this.apiService.getV2$<SystemDocument[]>(`api/Sso/dpa?token=${token}`, null, false).pipe(map((response) => response?.data));
    }

    markUserStale() {
        this._userState$.next(UserAuthState.stale);
    }

    getUser$base(): Observable<User> {
        if (this._userState$.value !== UserAuthState.userLoaded) {
            this._userState$.next(UserAuthState.loadingUser);
            return this.apiService.getV2$<User>('api/user').pipe(
                catchError((httpResponse) => {
                    this._userState$.next(UserAuthState.error);
                    // rethrow for further handling
                    throw httpResponse;
                }),
                map((response) => {
                    if (response?.data) {
                        this.setUser(response.data);
                        this._userState$.next(UserAuthState.userLoaded);
                        return response.data;
                    }
                    return null;
                })
            );
        } else {
            return this._user$.asObservable();
        }
    }

    getUser$(): Observable<User> {
        return this.getUser$base().pipe(
            catchError((err) => {
                this.apiService.apiErrorHandler(err);
                return of(null);
            })
        );
    }

    setFilterState(summaryType?: JacketType | 'Leads', jacketState?: DealStatuses[], leadState?: LeadStatus[]): void {
        const { value: filterState } = this.filterState;
        switch (summaryType) {
            case JacketType.Deal:
                sessionStorage.setItem('filter-state', JSON.stringify({ ...filterState, deals: jacketState }));
                this.filterState.next({ ...filterState, deals: jacketState });
                break;
            case JacketType.Document:
                sessionStorage.setItem('filter-state', JSON.stringify({ ...filterState, consumerDocs: jacketState }));
                this.filterState.next({ ...filterState, consumerDocs: jacketState });
                break;
            case JacketType.Storage:
                sessionStorage.setItem('filter-state', JSON.stringify({ ...filterState, storageJackets: jacketState }));
                this.filterState.next({ ...filterState, storageJackets: jacketState });
                break;
            case 'Leads':
                sessionStorage.setItem('filter-state', JSON.stringify({ ...filterState, leads: leadState }));
                this.filterState.next({ ...filterState, leads: leadState });
                break;
            default:
                sessionStorage.removeItem('filter-state');
                this.filterState.next({});
        }
    }

    getFilterState<T>(summaryType: JacketType | 'Leads'): T[] {
        switch (summaryType) {
            case JacketType.Deal:
                return this.filterState.value.deals as T[];
            case JacketType.Document:
                return this.filterState.value.consumerDocs as T[];
            case JacketType.Storage:
                return this.filterState.value.storageJackets as T[];
            case 'Leads':
                return this.filterState.value.leads as T[];
            default:
                return [];
        }
    }

    getUserByEmailForLogin$(email: string): Observable<User & UserDealershipTerminationDetails> {
        return this.apiService.getV1$<GetUserByEmailResponse>(`api/user/${email}`).pipe(
            catchError((httpResponse) => {
                this._userState$.next(UserAuthState.error);
                if (0 === httpResponse.status) {
                    // Could not connect to api server
                    this.errorService.showWhitelistError();
                    throw httpResponse;
                }

                // bad password, etc
                this.apiService.apiErrorHandler(httpResponse);
                return of(null);
            }),
            map((response) => {
                if (response?.data) {
                    const user = response.data.user;

                    if (user) {
                        this.setUser(user);
                        this._userState$.next(UserAuthState.loadingUser);
                        return {
                            ...user,
                            isDealershipTerminated: response.data.isDealershipTerminated,
                            hasOtherDealershipsAvailable: response.data.hasOtherDealershipsAvailable,
                        };
                    }
                }
                return null;
            })
        );
    }

    checkUserByEmail$(email: string): Observable<User> {
        const safeEmail = encodeURIComponent(email);
        return this.apiService.getV2$<User>(`api/user/check/${safeEmail}`).pipe(
            catchError((httpResponse) => {
                this._userState$.next(UserAuthState.error);
                this.apiService.apiErrorHandler(httpResponse);
                // rethrow for further handling
                throw httpResponse;
            }),
            map((response) => {
                return response?.data;
            })
        );
    }

    getUserByUserId$(dealerId: number, userId: number): Observable<User> {
        return this.apiService.getV2$(`api/user/get/${dealerId}/${userId}`).pipe(
            catchError((httpResponse) => {
                // rethrow for further handling
                this._userState$.next(UserAuthState.error);
                throw this.apiService.apiErrorHandler(httpResponse);
            }),
            map((response: { data: any; error?; message? }) => {
                if (response?.data) {
                    return response?.data;
                }
            })
        );
    }

    getUserSignature$(type: number): Observable<any> {
        return this.apiService.getBuffer$(`api/user/signature/${type}`).pipe(
            catchError((httpResponse) => {
                this.errorService.showError(httpResponse?.error?.message ? httpResponse.error.message : httpResponse);
                console.error(httpResponse?.error);
                return of(httpResponse);
            }),
            map((response) => {
                return response?.data?.Body ? response?.data?.Body : [];
            })
        );
    }

    getCurrentUserSignaturesPointData() {
        return this.apiService.getV2$('api/user/signature/point').pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    getOtherUserSignaturesPointData(user: User): Observable<{ userId: number; signature: string; initials: string }> {
        return this.apiService.getV2$(`api/user/signature/point/${user.dealerId}/${user.userId}`).pipe(
            map((response: any) => {
                return response.data;
            })
        );
    }

    deleteUser$(userId: number, dealerId: number): Observable<any> {
        return this.apiService.deleteV2$(`api/user/${userId}/${dealerId}`).pipe(
            catchError((httpResponse) => {
                return of({ error: httpResponse });
            }),
            map((response: any) => {
                return response;
            })
        );
    }

    sendHelpMessage(messageData: any): Observable<boolean> {
        return this.apiService.postV2$<boolean>('api/Messages/sendMessage', messageData).pipe(
            catchError((httpResponse) => {
                console.error(httpResponse);
                this.errorService.showError(httpResponse?.error?.message ? httpResponse.error.message : httpResponse);
                return of(null);
            }),
            map((response) => {
                return response?.data;
            })
        );
    }

    getUserFAQ$(type: string): Observable<any> {
        return this.apiService.getV2$(`api/Messages/${type}`).pipe(
            map((response: { data: any; error?; message? }) => {
                return response.data ? response.data : [];
            })
        );
    }

    resetUser(): void {
        this._user$.next(<User>{});
    }

    // if user hasn't logged in - sends welcome message
    // if user has logged in - sends reset password link
    sendResetUserLink(email: string): Observable<any> {
        return this.apiService.getV2$(`api/User/resetuser/${email}?isPhone=${true}&isEmail=${true}`).pipe(
            map((response: { data: any; error?; message? }) => {
                return response;
            })
        );
    }

    sendWelcomeNotification(email: string) {
        return this.apiService.getV2$<string>(`api/User/sendWelcomeNotification/${email}?isPhone=${true}&isEmail=${true}`).pipe(
            map((response) => {
                return response;
            })
        );
    }

    //This will erase user properties not listed below
    setUserCurrentDealer(dealerId: number): void {
        this._user$.next(<User>{
            dealerId: dealerId,
            dealerAppPermissions: this.user?.dealerAppPermissions || [],
            defaultPageId: this.user.defaultPageId,
            userId: this.user.userId,
            email: this.user.email,
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            legalName: this.user.legalName,
            middleName: this.user.middleName,
            phone: this.user.phone,
            userType: this.user.userType,
            needProfileUpdate: this.user.needProfileUpdate,
            needToAcceptSystemDocument: this.user.needToAcceptSystemDocument,
            emailAlerts: this.user.emailAlerts,
            textAlerts: this.user.textAlerts,
            signature: this.user.signature,
            initials: this.user.initials,
        });
    }

    setUser(user: User): void {
        // use the new user's dealerId, if available
        let dealerId = user.dealerId;
        if (!dealerId) {
            dealerId = !this.user.dealerId ? (user?.dealerAppPermissions || []).find((dealer) => dealer.homeDealer === true)?.dealerId : this.user.dealerId;
        }

        this._user$.next(<User>{
            dealerId: dealerId,
            dealerAppPermissions: user?.dealerAppPermissions || [],
            defaultPageId: user.defaultPageId,
            userId: user.userId,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            legalName: user.legalName,
            middleName: user.middleName,
            logonId: user.logonId,
            phone: user.phone,
            userType: user.userType,
            needProfileUpdate: user.needProfileUpdate,
            emailAlerts: user.emailAlerts,
            textAlerts: user.textAlerts,
            signature: user.signature,
            initials: user.initials,
            active: user.active,
            sendToWelcomeFlow: user.sendToWelcomeFlow,
            needToAcceptSystemDocument: user.needToAcceptSystemDocument,
        });
    }

    // Needed for admin user update. If user updates themselves
    // permissions are not being passed back on that save
    setUserMaintainPermissions(user: any): void {
        const dealerId = !this.user.dealerId ? (user?.dealerAppPermissions || []).find((dealer) => dealer.homeDealer === true).dealerId : this.user.dealerId;

        this._user$.next(<User>{
            dealerId,
            dealerAppPermissions: this.user?.dealerAppPermissions || [],
            userId: user.userId,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            legalName: user.legalName,
            middleName: user.middleName,
            logonId: user.logonId,
            phone: user.phone,
            userType: user.userType,
            needProfileUpdate: user.needProfileUpdate,
            emailAlerts: user.emailAlerts,
            textAlerts: user.textAlerts,
            signature: user.signature,
            initials: user.initials,
            active: user.active,
        });
    }

    updateRequireSystemDoc(systemDocumentId: number): Observable<SystemDocument[]> {
        return this.apiService.postV2$<SystemDocument[]>(`api/user/systemDocuments/${systemDocumentId}`, null).pipe(
            map((response) => {
                return response?.data || [];
            })
        );
    }

    updateUser$(
        email: string,
        firstName: string,
        lastName: string,
        phone: string,
        middleName?: string,
        dealerId?: number,
        defaultPageId?: number
    ): Observable<User> {
        return this.apiService
            .putV2$<User>('api/user', {
                dealerId: this.getAppropriateDealerId(dealerId),
                email,
                lastName: lastName,
                firstName: firstName,
                phone: phone,
                middleName: middleName,
                notification: this.userNotificationType,
                defaultPageId: defaultPageId ? defaultPageId : null,
            })
            .pipe(
                catchError((error) => {
                    return of(null);
                }),
                map((response) => {
                    if (response.data) {
                        this.setUser(response.data);
                        return response.data;
                    }
                    return null;
                })
            );
    }

    updateUserNotification$(email: string, firstName: string, lastName: string, phone: string, notification: string, dealerId?: number): Observable<User> {
        return this.apiService
            .putV2$('api/user/notifications', {
                dealerId: this.getAppropriateDealerId(dealerId),
                email: email,
                lastName: lastName,
                firstName: firstName,
                phone: phone,
                notification: UserNotificationType[notification],
                dealerAppPermissions: this.user?.dealerAppPermissions || [],
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response: { data: any; error?; message? }) => {
                    if (response.data) {
                        this.setUser(response.data);
                        return response.data;
                    }
                })
            );
    }

    getAppropriateDealerId(dealerId?: number): number {
        //This seems like a bit much. Could the be simplified by getting the dealerId from the dealership service?
        //users can now have no permissions assigned which means that a home dealer will not be found. So...return the dealerId on the user
        let dlrId = !dealerId ? (this.user?.dealerAppPermissions || []).find((dealer) => dealer?.homeDealer === true)?.dealerId : dealerId;

        if (!dlrId) {
            if (!dealerId) dlrId = this.user?.dealerId;
        }
        return dlrId;
    }

    updateLastLogon$(user: User, userLogonState: UserLogonState, mfaDevice?: string): Observable<boolean> {
        return this.apiService
            .putV2$<boolean>('api/user/logon', {
                userLogonState: userLogonState,
                logonType: mfaDevice ? 'MFA' : 'Logon',
                MFADevice: mfaDevice,
            })
            .pipe(
                catchError((httpResponse) => {
                    this._userState$.next(UserAuthState.error);
                    console.error(httpResponse?.error);
                    this.errorService.showError(httpResponse?.error?.message ? httpResponse.error.message : httpResponse);
                    return of(httpResponse as RpcApiResponse<boolean>);
                }),
                map((response) => {
                    return response?.data;
                })
            );
    }

    createSignature$(type: UserSignatureCall, body: any): Observable<User> {
        return this.apiService.postV2$<User>(`api/user/signature/${type}`, body).pipe(
            catchError((httpResponse) => {
                console.error(httpResponse?.error);
                this.errorService.showError(httpResponse?.error?.message ? httpResponse.error.message : httpResponse);
                return of();
            }),
            map((response) => {
                return response?.data;
            })
        );
    }

    getJobTitles(): Observable<JobTitle[]> {
        return this.apiService.getV2$<JobTitle[]>('api/user/jobTitles').pipe(
            map((response) => {
                return response?.data || [];
            })
        );
    }

    getDefaultPages(): Observable<any[]> {
        return this.apiService.getV2$<any[]>('api/user/defaultPages').pipe(
            map((response) => {
                return response?.data || [];
            })
        );
    }

    getDealersList$(search: string, page = 1, records = 100, oem: string, autoGroup?: string, dealerStatus?: DealerStatus): Observable<DealerData> {
        return this.apiService
            .getV2$<DealerData>(
                `api/user/dealerManagementDealers?search=${search}&page=${page}&records=${records}&oem=${oem}${autoGroup ? `&autoGroup=${autoGroup}` : ''}${
                    dealerStatus ? `&dealerStatus=${dealerStatus}` : ''
                }`
            )
            .pipe(
                map((response) => {
                    return response?.data || { count: 0, dealers: [] };
                })
            );
    }

    getDealerUserList(dealerId: number, userTypeId: UserTypes = null, search = '', page = 1, records = 25, sortBy = '', sortAsc = false) {
        const searchPart = search !== '' ? '&search=' + search : '';
        const userTypePart = userTypeId === null ? '' : '&userTypeId=' + userTypeId;
        return this.apiService
            .getV2$<
                PaginatedData & { users: User[] }
            >(`api/user/admin/list/${dealerId}?page=${page}&records=${records}${searchPart}${userTypePart}&sortBy=${sortBy}&sortAsc=${sortAsc}`)
            .pipe(map((response) => (response.data ? response.data : { users: [] })));
    }

    getPermissions(dealerId: number, userId?: string): Observable<AppPermission[]> {
        return this.apiService.getV2$<AppPermission[]>(`api/user/admin/permissions/${dealerId}/${userId}`).pipe(
            map((response) => {
                return response.data ? response.data : [];
            })
        );
    }

    updatePermissions(userId: number, dealerId: number, dealerAppPermissions: DealerAppPermissions[], email: string) {
        return this.apiService
            .putV2$('api/user/admin/permissions', {
                userId,
                dealerId: this.getAppropriateDealerId(dealerId),
                email,
                dealerAppPermissions,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response: { data: any; error?; message? }) => {
                    if (response && response.data) {
                        if (this.user.userId === response.data.userId) {
                            this.setUser(response.data);
                        }
                        return response.data;
                    }
                })
            );
    }

    adminUpdateUserOnDeal$(
        userId: number,
        logonId: string,
        email: string,
        firstName: string,
        lastName: string,
        phone: string,
        dealId: number,
        userType: string,
        middleName?: string,
        enableMFA?: boolean,
        dealerId?: number,
        userActive?: boolean
    ): Observable<User> {
        return this.apiService
            .putV2$(`api/user/admin/update/dealUser?dealId=${dealId}`, {
                userId,
                logonId,
                dealerId: this.getAppropriateDealerId(dealerId),
                email,
                lastName: lastName,
                firstName: firstName,
                phone: phone,
                enableMFA: enableMFA,
                userType: userType === 'buyer' ? UserTypes.Buyer : UserTypes.CoBuyer,
                middleName: middleName,
                active: userActive,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response: { data: any; error?; message? }) => {
                    if (response && response.data) {
                        if (this.user.userId === response.data.userId) {
                            this.setUserMaintainPermissions(response.data);
                        }
                        return response.data;
                    }
                })
            );
    }

    adminUpdateUser$(
        userId: number,
        email: string,
        firstName: string,
        lastName: string,
        phone: string,
        systemAdministratorAtDealerId: boolean,
        middleName?: string,
        dealerId?: number,
        externalUserId?: string,
        enableMFA?: boolean,
        userActive?: boolean,
        userType?: UserTypes,
        jobTitle?: string
    ): Observable<User> {
        return this.apiService
            .putV2$('api/user/admin/update', {
                userId,
                dealerId: this.getAppropriateDealerId(dealerId),
                email,
                lastName,
                middleName,
                firstName,
                phone,
                systemAdministratorAtDealerId,
                externalUserId,
                enableMFA,
                userType: userType ?? UserTypes.Dealer,
                jobTitle,
                active: userActive,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response: { data: any; error?; message? }) => {
                    if (response && response.data) {
                        if (this.user.userId === response.data.userId) {
                            this.setUserMaintainPermissions(response.data);
                        }
                        return response.data;
                    }
                })
            );
    }

    adminCreateUser$(
        dealerId: number,
        email: string,
        firstName: string,
        lastName: string,
        phone: string,
        dealerAppPermissions: DealerAppPermissions[],
        systemAdministratorAtDealerId: boolean,
        middleName?: string,
        externalUserId?: string,
        enableMFA?: boolean,
        enableUser?: boolean,
        internalUser?: boolean,
        jobTitle?: string
    ) {
        return this.apiService
            .postV2$<{ user: User }>('api/user', {
                dealerId: this.getAppropriateDealerId(dealerId),
                email,
                lastName: lastName,
                firstName: firstName,
                phone: phone,
                dealerAppPermissions,
                systemAdministratorAtDealerId: systemAdministratorAtDealerId,
                middleName: middleName,
                role: internalUser ? 'Internal' : 'Dealer',
                notification: UserNotificationType.Both,
                externalUserId: externalUserId,
                internalUser: internalUser,
                userType: internalUser ? UserTypes.Internal : UserTypes.Dealer,
                enableMFA: enableMFA,
                jobTitle: jobTitle,
                active: enableUser,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response: { data: { user: User }; error?; message? }) => {
                    if (response && response.data) {
                        return response.data;
                    }

                    return null;
                })
            );
    }

    ssoCreateUser$(dealerId: number, email: string, firstName: string, lastName: string, phone: string, jobTitle: string) {
        return this.apiService
            .postUnauthenticatedV2('api/sso/user', {
                dealerId,
                email,
                lastName: lastName,
                firstName: firstName,
                phone: phone,
                jobTitle: jobTitle,
                notification: UserNotificationType.Both,
                ssoDealerOnboarding: true,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response: { data: any; error?; message? }) => {
                    if (response && response.data) {
                        return response.data;
                    }
                })
            );
    }

    profileChangePassword(user: User, oldPassword: string, newPassword: string): Observable<{ success: boolean; errorMsg?: string }> {
        return new Observable<{ success: boolean; errorMsg?: string }>((observer) => {
            this.authService.changePassword(oldPassword, newPassword, (err, result) => {
                if (err) {
                    let errorMsg = '';
                    switch (err.name) {
                        case 'LimitExceededException':
                            errorMsg = 'Maximum number of attempts reached. Please try again later.';
                            break;
                        case 'NotAuthorizedException':
                        default:
                            errorMsg = 'Current password is incorrect. Please try again.';
                    }
                    this.loginService
                        .logLoginState(user.userId, user.logonId, UserLogonState.PasswordChangeFailure, 'Password Change Failure')
                        .subscribe(() => {
                            observer.next({ success: false, errorMsg });
                            observer.complete();
                        });
                } else {
                    this.snackbarService.openSnackbar('success', 'Password changed successfully.');
                    this.loginService
                        .logLoginState(user.userId, user.logonId, UserLogonState.PasswordChangeSuccess, 'Password Change Success')
                        .subscribe(() => {
                            observer.next({ success: true });
                            observer.complete();
                        });
                }
            });
        });
    }

    setTemporaryPassword(newPassword: string, logonId: string) {
        const pwData = new FormData();
        pwData.append('newPassword', newPassword);
        pwData.append('logonId', logonId);

        return this.apiService.putV2$('api/User/setTemporaryPassword/', pwData).pipe(
            catchError((error) => {
                return of(error);
            }),
            map((response: { data?: boolean; error?; message? }) => {
                if (response && response.data) {
                    return response.data;
                }

                return null;
            })
        );
    }

    resetPassword(email: string, isPhone = false) {
        //email is expected here as the API will lookup the logonid

        return this.apiService
            .getV2$<'Admin Reset Success' | 'Pin Success'>(`api/user/resetuser/${email}?isPhone=${isPhone}&isEmail=${!isPhone}`, null, false)
            .pipe(
                map((response) => {
                    return response?.data;
                })
            );
    }

    checkAndHandleMissingSignature() {
        // Since the needsProfileUpdate field checks the point data
        // fields, we now just rely on that field to determine if we
        // need to prompt this modal
        if (this.user?.needProfileUpdate) {
            this.dialog
                .open(ChangeSignatureModalComponent, {
                    closeOnNavigation: false,
                    disableClose: true,
                    width: this.deviceService.isDesktop() ? '700px' : '100vw',
                    height: '100vh',
                    data: {
                        user: this.user,
                    },
                })
                .afterClosed()
                .subscribe(async (data) => {
                    let newUser = this.user;

                    if (data.signature) {
                        const formDataSignature: FormData = await formatSignatureRequest('signature', data, this.user.dealerId);

                        // Need to subscribe in order for API call to go through
                        newUser = await firstValueFrom(this.createSignature$(UserSignatureCall.signature, formDataSignature));
                    }

                    if (data.initials) {
                        const formDataInitials: FormData = await formatSignatureRequest('initials', data, this.user.dealerId);

                        // Need to subscribe in order for API call to go through
                        newUser = await firstValueFrom(this.createSignature$(UserSignatureCall.initials, formDataInitials));
                    }

                    // We need to update the user so it has the initials and signature fields populated.
                    // If we don't they may try and sign a document and get met with the invalid signature dialog
                    this.setUser(newUser);
                });
        }
    }
}
